import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Plus, Sparkles, Flame } from 'lucide-react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import confetti from 'canvas-confetti';
import { CircularProgress } from '@/components/CircularProgress';
import { MacroCard } from '@/components/MacroCard';
import { useUserStore } from '@/store/userStore';
import { useDayMeals } from '@/hooks/useMeals';
import StepsCard from '@/components/StepsCard';
import { WeightCard } from '@/components/WeightCard';
import { DofiStartCard } from '@/components/DofiStartCard';
import SlavaLine from '@/components/SlavaLine';
import { useTodaySteps } from '@/hooks/useSteps';
import { useTelegram } from '@/hooks/useTelegram';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { getActiveTargets, fmtNum } from '@/utils/format';
import { Avatar } from '@/components/Avatar';
import { AgeFocusCard } from '@/components/AgeFocusCard';
import { MicronutrientsCard } from '@/components/MicronutrientsCard';
import { CourseTodayCard } from '@/components/CourseTodayCard';
import { DailyCheckinCard } from '@/components/DailyCheckinCard';
import { RationWeekCard } from '@/components/RationWeekCard';
import { HabitsCard } from '@/components/HabitsCard';
import { VkusvillNudge } from '@/components/VkusvillNudge';
import { DemoPhotoCard } from '@/components/DemoPhotoCard';
import { WaterCard } from '@/components/WaterCard';
import { PartnerInbox } from '@/components/PartnerInbox';
// Компонент для плавно перекатывающихся чисел
function AnimatedNumber({ value }: { value: number }) {
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, (latest) => Math.round(latest));
  useEffect(() => {
    const controls = animate(motionValue, value, {
      duration: 0.8,
      ease: [0.32, 0.72, 0, 1], // iOS-easing
    });
    return controls.stop;
  }, [value, motionValue]);
  return <motion.span>{rounded}</motion.span>;
}
export default function Dashboard() {
  const nav = useNavigate();
  const { profile } = useUserStore();
  const { totals } = useDayMeals();
  const { steps, refetch: refetchSteps } = useTodaySteps();
  const { haptic } = useTelegram();
  // Конфетти при достижении цели по белку
  const [proteinCelebrated, setProteinCelebrated] = useState(false);
  const [showProteinToast, setShowProteinToast] = useState(false);
  useEffect(() => {
    if (!profile) return;
    
    const currentTargets = getActiveTargets(profile);
    const proteinReached = totals.protein >= currentTargets.protein;
    
    if (proteinReached && !proteinCelebrated) {
      confetti({
        particleCount: 80,
        spread: 60,
        origin: { x: 0.2, y: 0.6 },
        colors: ['#10b981', '#06b6d4', '#84cc16'],
      });
      
      setTimeout(() => {
        confetti({
          particleCount: 80,
          spread: 60,
          origin: { x: 0.8, y: 0.6 },
          colors: ['#10b981', '#06b6d4', '#84cc16'],
        });
      }, 200);
      
      setProteinCelebrated(true);
      setShowProteinToast(true);
      // Скрыть тост через 3 секунды
  setTimeout(() => {
    setShowProteinToast(false);
  }, 3000);
}
    
    if (!proteinReached && proteinCelebrated) {
      setProteinCelebrated(false);
    }
  }, [totals.protein, proteinCelebrated, profile]);
  if (!profile) return null;
  const targets = getActiveTargets(profile);
  const today = format(new Date(), 'd MMMM, EEEE', { locale: ru });
  const greetHour = new Date().getHours();
  const greeting =
    greetHour < 5 ? 'Доброй ночи,'
    : greetHour < 12 ? 'Доброе утро,'
    : greetHour < 18 ? 'Добрый день,'
    : 'Добрый вечер,';
  
  // Строка «−N ккал расход» под кольцом убрана (решение Славы 22.09.2026):
  // активность с телефона и часов не синхронизируется, поэтому «расход» был
  // почти целиком расчётным обменом и оказывался БОЛЬШЕ нормы (−2479 при
  // норме 2090). Это сбивало с толку и подталкивало к мысли «можно съесть
  // больше». Кольцо и норма не менялись.
  
  const goAdd = (mode: 'photo' | 'text' | 'manual') => {
    haptic('medium');
    nav(`/add?mode=${mode}`);
  };
  // Без pt-6 у контейнера: он отжимал sticky-шапку от верха, и она не могла
  // прилипнуть. Отступ перенесён внутрь самой шапки.
  return (
    <div className="px-5 pb-28 max-w-md mx-auto">
      {/* Toast при достижении цели по белку */}
{showProteinToast && (
  <motion.div
    initial={{ opacity: 0, y: -20, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ type: "spring", stiffness: 400, damping: 25 }}
    className="fixed top-20 left-1/2 -translate-x-1/2 z-50
      bg-emerald-glow/90 backdrop-blur-md
      text-bg font-medium
      px-4 py-3 rounded-2xl shadow-lg
      flex items-center gap-2"
  >
    <span className="text-xl">🎉</span>
    <div>
      <div className="font-bold text-sm">Цель по белку достигнута!</div>
      <div className="text-xs opacity-80">{Math.round(totals.protein)}г / {targets.protein}г белка</div>
    </div>
  </motion.div>
)}
      {/* Header — sticky glass morphism */}
      <div className="sticky top-0 z-30 -mx-5 px-5 mb-5 pt-4 pb-3
        backdrop-blur-xl bg-bg/75
        border-b border-line/20">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-ink-dim uppercase tracking-wider">Сегодня</div>
            <div className="font-display text-xl mt-0.5">{today}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-xs text-ink-dim">{greeting}</div>
              <div className="font-medium">{profile.first_name ?? 'друг'}</div>
            </div>
            <Avatar url={profile.avatar_url} name={profile.first_name} size="md" />
          </div>
        </div>
      </div>

      {/* Подписка — громкость по сроку (решение Славы 03.09.2026). В Профиль
          не убрана намеренно: там о ней узнавали бы уже после того, как
          развернуло на оплату. Далеко — тихая строка, не мешает; семь дней
          и меньше — заметный чип с кнопкой, не пропустишь. */}
      {profile.subscription_expires_at &&
        new Date(profile.subscription_expires_at) > new Date() && (() => {
          const daysLeft = Math.ceil((new Date(profile.subscription_expires_at).getTime() - Date.now()) / 86400000);
          const label = profile.subscription_status === 'trial' ? 'Пробный период' : 'Подписка';
          const dateStr = format(new Date(profile.subscription_expires_at), 'd MMMM', { locale: ru });
          if (daysLeft <= 7) {
            return (
              <button
                onClick={() => nav('/paywall')}
                className="w-full flex items-center justify-between gap-2 mb-3 px-3 py-2 rounded-xl border active:opacity-80"
                style={{ borderColor: 'rgba(255,180,84,0.45)', background: 'rgba(255,180,84,0.08)' }}
              >
                <span className="text-xs text-warn font-medium">
                  {label} {daysLeft <= 0 ? 'заканчивается сегодня' : daysLeft === 1 ? 'заканчивается завтра' : `заканчивается через ${daysLeft} дн.`} · {dateStr}
                </span>
                <span className="shrink-0 text-[11px] font-bold text-bg bg-warn px-2.5 py-1 rounded-lg">Продлить</span>
              </button>
            );
          }
          return (
            <button
              onClick={() => nav('/paywall')}
              className="w-full flex items-center justify-center gap-1.5 text-[11px] text-ink-dim mb-3 active:opacity-70"
            >
              <Sparkles size={11} className="text-emerald-glow/70" />
              {label} до {dateStr}
            </button>
          );
        })()}

      {/* Твой шаг сегодня — вход в «Путь со Славой» */}
      <CourseTodayCard />

      {/* «Попробуй на примере» — пока нет ни одной записи еды (22.09.2026) */}
      <DemoPhotoCard />

      {/* Круг калорий */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="flex justify-center mb-2"
      >
        <CircularProgress consumed={totals.calories} goal={targets.calories} />
      </motion.div>
      
      <div className="text-center mb-2">
        <div className="font-mono text-sm text-ink-muted">
          <span className="text-ink"><AnimatedNumber value={totals.calories} /></span> / <span>{targets.calories}</span> ккал
        </div>
      </div>
      
      
      {/* Макросы */}
      <div className="flex gap-2 mb-4">
        <MacroCard label="Белки" consumed={totals.protein} goal={targets.protein} color="emerald" />
        <MacroCard label="Жиры" consumed={totals.fat} goal={targets.fat} color="warn" />
        <MacroCard label="Углеводы" consumed={totals.carbs} goal={targets.carbs} color="sky" />
      </div>

      {/* ✨ Слава — одна фраза-действие по данным дня. Не окно, а строка;
          молчит, когда сказать нечего; «помолчи» гасит насовсем.
          Правила хартии ненавязчивости — в SlavaLine.tsx. */}
      <SlavaLine totals={totals} targets={targets} steps={steps} />
      {/* «Старт с Дофи» — задание дня (30.10.2026) */}
      <DofiStartCard place="home" />

      {/* CTA — запись еды. Поднято над шаги/воду (решение Славы 03.09.2026):
          главное действие приложения было девятым блоком сверху, под сгибом
          экрана, и человек скроллил, чтобы сделать то, зачем пришёл. */}
      <div className="mb-2 text-xs text-ink-dim">Записать приём еды</div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <ActionTile Icon={Camera} label="Фото" accent="emerald" onClick={() => goAdd('photo')} />
        <ActionTile Icon={Sparkles} label="Текст" accent="sky" onClick={() => goAdd('text')} />
        <ActionTile Icon={Plus} label="Вручную" accent="muted" onClick={() => goAdd('manual')} />
      </div>

      {/* Вечером, если до нормы не добрано: собрать корзину во ВкусВилле.
          Правила показа — в VkusvillNudge.tsx. */}
      <VkusvillNudge totals={totals} targets={targets} />

      {/* Присланное партнёром — выше воды: требует действия, а вода нет */}
      <PartnerInbox />

      {/* Шаги — строкой, и ВСЕГДА видны, даже с нулём. Раньше строка появлялась
          только при steps > 0, а вносить их можно было лишь в Профиле: кто ни разу
          не вносил, не знал, что это вообще есть. Замерено 02.09.2026 — 4 записи
          о шагах за неделю на двоих из 43 человек.
          Стоят НАД водой: две однотипные строки читаются как один блок про тело
          за день, и шаги идут первыми — их отмечают одним числом за раз,
          а воду доливают по ходу дня. */}
      <StepsCard steps={steps} onSaved={refetchSteps} />

      {/* Вода — второй строкой того же блока: смотрят мельком, но отмечают часто */}
      <WaterCard />

      {/* Вес — третьей строкой, но НЕ каждый день. Мерил 21 человек, а за
          последний месяц двое: ввод жил только в Профиле, как когда-то шаги.
          Появляется раз в неделю: ежедневное взвешивание для худеющего —
          путь к тревоге, вес скачет на воде и соли, и человек ловит шум
          как провалы. Неделя — интервал, на котором виден тренд. */}
      <WeightCard />

      {/* Отметка дня — сон утром, «как день» вечером */}
      <DailyCheckinCard />

      {/* Привычки с Дофи — вечер в три кнопки (22.09.2026) */}
      <HabitsCard />
        <RationWeekCard />

      {/* 💎 Микронутриенты */}
      <div className="mb-6">
        <MicronutrientsCard />
      </div>
      
      {/* Возрастной фокус — только для 50+ */}
      <AgeFocusCard
        totalProteinG={totals.protein}
        totalCaloriesKcal={totals.calories}
        targetCaloriesKcal={targets.calories}
      />
      
      {/* Геймификация */}
      <GamificationCard profile={profile} />

      {/* Плато */}
      <PlateauBanner profile={profile} />
    </div>
  );
}
function GamificationCard({ profile }: { profile: any }) {
  const nav = useNavigate();
  if (!profile) return null;
  // Уровень и XP убраны с глаз (решение Славы 03.09.2026): уровень ничего
  // не открывал, XP — внутренняя валюта, которая не значит ничего ни внутри,
  // ни снаружи. 34 из 43 сидели на уровне 1–2. Осталось единственное живое —
  // серия дней подряд: это поведение, а не счётчик кликов.
  const streak = profile.current_streak_meals ?? 0;
  const record = profile.longest_streak_meals ?? 0;
  if (streak <= 0) return null;
  const dayWord = streak === 1 ? 'день' : streak < 5 ? 'дня' : 'дней';
  return (
    <button
      onClick={() => nav('/achievements')}
      className="w-full card px-3 py-2.5 mb-4 flex items-center gap-3 hover:bg-bg-elevated transition-colors active:scale-[0.98]"
    >
      <div className="w-9 h-9 rounded-xl bg-warn/15 flex items-center justify-center shrink-0">
        <Flame size={16} className="text-warn" />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <div className="font-display font-bold text-sm">{streak} {dayWord} подряд</div>
        {record > streak && (
          <div className="text-[10px] text-ink-dim">рекорд {record}</div>
        )}
        {record > 0 && record <= streak && (
          <div className="text-[10px] text-emerald-glow">это твой рекорд</div>
        )}
      </div>
      <div className="text-[10px] text-ink-dim">достижения →</div>
    </button>
  );
}
function PlateauBanner({ profile }: { profile: any }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed || !profile?.last_weight_change_at) return null;
  const daysSince = Math.floor(
    (Date.now() - new Date(profile.last_weight_change_at).getTime()) /
    (1000 * 60 * 60 * 24)
  );
  if (daysSince < 14) return null;
  const currentCarbs = profile.daily_carbs_g ?? 250;
  const newCarbs = Math.round(currentCarbs * 0.9);
  const calDiff = Math.round((currentCarbs - newCarbs) * 4);
  return (
    <div className="card border-warn/30 bg-warn/5 p-4 mt-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">⚖️</span>
            <span className="font-display font-semibold text-sm text-warn">
              Вес стоит {daysSince} дней
            </span>
          </div>
          <p className="text-xs text-ink-muted leading-relaxed">
            Попробуй повысить активность или снизить углеводы на 10% —
            убери ~{calDiff} ккал за счёт углеводов (до {newCarbs}г/день).
          </p>
        </div>
        <button onClick={() => setDismissed(true)} className="text-ink-dim text-xs shrink-0 mt-1">✕</button>
      </div>
    </div>
  );
}
function ActionTile({
  Icon, label, accent, onClick,
}: {
  Icon: typeof Camera;
  label: string;
  accent: 'emerald' | 'sky' | 'muted';
  onClick: () => void;
}) {
  const colors = {
    emerald: 'text-emerald-glow border-emerald-glow/30 hover:bg-emerald-glow/5 shadow-[0_0_18px_-6px_rgba(16,229,142,0.55)]',
    sky: 'text-sky-glow border-sky-glow/30 hover:bg-sky-glow/5 shadow-[0_0_18px_-6px_rgba(61,216,255,0.55)]',
    muted: 'text-ink border-line hover:bg-bg-elevated',
  };
  return (
    <motion.button
      onClick={onClick}
      whileTap={{ scale: 0.94 }}
      transition={{ type: "spring", stiffness: 400, damping: 17 }}
      className={`card flex flex-col items-center gap-2 py-4 ${colors[accent]}`}
    >
      <Icon size={22} />
      <span className="text-sm font-medium">{label}</span>
    </motion.button>
  );
}
